'use client';

import {
	BASE_PRECISION_EXP,
	BN,
	BigNum,
	OrderType,
	PRICE_PRECISION_EXP,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	SpotBalanceType,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UISerializableOrder,
	MarketId,
	UIMarket,
	matchEnum,
	roundToStepSizeIfLargeEnough,
	getMaxLeverageOrderSize,
	isEntirePositionOrder,
	getUIOrderTypeFromSdkOrderType,
	trimTrailingZeros,
} from '@velocity-exchange/common';
import { Swap } from '@velocity-exchange/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZERO } from 'src/constants/math';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import useAccountSpotBalances from 'src/hooks/useAccountBankBalances';
import useOraclePrice from 'src/hooks/useOraclePrice';
import {
	getMessageInfoForOrderViolation,
	getPriceInputLabel,
	getShouldShowSecondaryPriceBox,
} from 'src/utils/trade';
import { isStopLossBeyondLiquidationPrice } from 'src/utils/tpSlValidation';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import { getQuotePriceToUse } from 'src/hooks/positions/useClosePositionStateEngine';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useMarkPrice from 'src/hooks/useMarkPrice';
import {
	useSyncPerpMaxLeverageAndFundsBreakdown,
	UseSyncPerpMaxLeverageAndFundsBreakdownTradeData,
} from 'src/components/TradeForm/hooks/useSyncPerpMaxLeverageAndFundsBreakdown';
import useDriftClient from 'src/hooks/useDriftClient';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import PriceInput from 'src/components/TradeForm/PriceInput/PriceInput';
import SizeInput from 'src/components/TradeForm/SizeInput/SizeInput';
import Slider from 'src/components/primitives/Slider/Slider';
import Button from 'src/components/primitives/Button/Button';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import TpSlOrderFormEstimatedPnL from 'src/components/TradeForm/TpSlOrderForm/TpSlOrderFormEstimatedPnL';
import ValueDisplay from 'src/components/ValueDisplay';
import { TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME } from 'src/components/TradeConfirmationModal/consts';
import { useEditNonMarketOrder } from 'src/hooks/trade/edit/useEditNonMarketOrder';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import MaxSizeLabel from 'src/components/TradeForm/MaxSizeLabel';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import { ITradeForm } from 'src/stores/DriftStore/types/tradeForm';

type Props = {
	order: UISerializableOrder;
	onClose: () => void;
	newPrice?: BigNum;
};

const directionLabelsPerp = {
	long: 'long',
	short: 'short',
};

const directionLabelsSpot = {
	long: 'buy',
	short: 'sell',
};

export const EditOrderModalContent = ({ order, onClose, newPrice }: Props) => {
	const driftClient = useDriftClient();
	const currentUserClient = driftClient?.hasUser()
		? driftClient.getUser()
		: undefined;
	const marketType = order.marketType;
	const marketIndex = order.marketIndex;
	const marketId = new MarketId(marketIndex, marketType);
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const marginMode =
		useMarketSettingsStore((s) =>
			s.getMarginModeForMarket(marketId.key, currentUserKey ?? '')
		) ?? 'cross';
	const uiMarket = UIMarket.fromMarketId(marketId);
	const isSpot = uiMarket.isSpot;
	const side: ITradeForm['side'] = ENUM_UTILS.match(
		order?.direction,
		PositionDirection.LONG
	)
		? 'buy'
		: 'sell';
	const orderIsLong = side === 'buy';
	const baseAssetSymbol = uiMarket.uiSymbols.baseAssetSymbol;

	const existingOrderType = getUIOrderTypeFromSdkOrderType(
		order.orderType,
		order.triggerCondition,
		order.direction,
		order.oraclePriceOffset
	);
	const existingOrderLimitPriceString = trimTrailingZeros(
		order.price.toPrecision(QUOTE_PRECISION_EXP.toNumber()).toString()
	);
	const existingOrderOracleOffset = order.oraclePriceOffset
		? trimTrailingZeros(
				order.oraclePriceOffset
					?.toPrecision(QUOTE_PRECISION_EXP.toNumber())
					.toString()
			)
		: undefined;

	const existingOrderTriggerPriceString = trimTrailingZeros(
		order.triggerPrice?.toPrecision(QUOTE_PRECISION_EXP.toNumber()).toString()
	);
	const existingOrderSizeStringValue = trimTrailingZeros(
		order.baseAssetAmount?.sub(order?.baseAssetAmountFilled).toTradePrecision()
	);

	const existingOrderDirectionPerp: string =
		directionLabelsPerp[
			Object.keys(order.direction)[0] as keyof typeof directionLabelsPerp
		] || '';
	const existingOrderDirectionSpot: string =
		directionLabelsSpot[
			Object.keys(order.direction)[0] as keyof typeof directionLabelsSpot
		] || '';

	const isLimitOnlyOrder = existingOrderType?.value === 'limit';
	const isTriggerOrder = [
		'stopMarket',
		'stopLimit',
		'takeProfitMarket',
		'takeProfitLimit',
	].includes(existingOrderType.value);
	const isTriggerLimitOrder = ['stopLimit', 'takeProfitLimit'].includes(
		existingOrderType?.value
	);
	const isOracleOffsetOrder = existingOrderType.value === 'oracleLimit';

	const marketPriceDecimals = getMarketPriceDecimals(baseAssetSymbol);

	// For oracle offset orders, the size should auto-update relative to the oracle price
	// if the user sets the size via the slider (percentage). So we use this ref to track
	// the source of the current size value and update when it's been set from the slider.
	const sizeSourceRef = useRef<'input' | 'slider'>('input');

	const showSecondaryPriceBox = getShouldShowSecondaryPriceBox(
		existingOrderType.value
	);

	const priceInputLabel = getPriceInputLabel(existingOrderType.value);

	const getInitialPrimaryPrice = () => {
		if (newPrice) {
			return newPrice.print();
		}

		if (isTriggerOrder) {
			return existingOrderTriggerPriceString;
		}

		if (isLimitOnlyOrder) {
			return existingOrderLimitPriceString;
		}

		if (existingOrderType.value === 'oracleLimit') {
			return existingOrderOracleOffset as string;
		}

		return '';
	};

	const getInitialSecondaryPrice = () => {
		if (isTriggerOrder) {
			return existingOrderLimitPriceString;
		}

		return '';
	};

	const accountData = useAccountData();
	const currentUserPerpPositions = useCurrentAccountPositions();
	const currentUserSpotBalances = useAccountSpotBalances(currentUserKey);
	const { editNonMarketOrder } = useEditNonMarketOrder();

	const oraclePrice = useOraclePrice(marketId);
	const stepSize = useMarketStepSize(marketId);

	const previewOrderParams = usePreviewOrder(marketId);

	const markPriceToUse = useMarkPrice(marketId);
	const openPositionInfo = currentUserPerpPositions?.find(
		(position) =>
			position.marketIndex === marketIndex && !position.baseSize.isZero()
	);
	const spotBalance = currentUserSpotBalances.find(
		(balance) => balance.asset.marketIndex === marketIndex
	);
	const currentPositionSide = isSpot
		? ENUM_UTILS.match(spotBalance?.balanceType, SpotBalanceType.DEPOSIT)
			? 'long'
			: 'short'
		: openPositionInfo?.direction;

	const [baseSizeStringValue, setBaseSizeStringValue] = useState(
		existingOrderSizeStringValue
	);

	const [priceBoxStringValue, setPriceBoxStringValue] = useState(
		getInitialPrimaryPrice() as string
	);
	const [secondaryPriceBoxStringValue, setSecondaryPriceBoxStringValue] =
		useState(getInitialSecondaryPrice());

	const [submitting, setSubmitting] = useState(false);
	const [inputSource, setInputSource] = useSizeInputSource();

	const priceBoxValueBigNum = BigNum.fromPrint(
		priceBoxStringValue as string,
		PRICE_PRECISION_EXP
	);
	const secondaryPriceBoxValueBigNum = BigNum.fromPrint(
		secondaryPriceBoxStringValue,
		PRICE_PRECISION_EXP
	);

	const baseSizeBigNum = BigNum.fromPrint(
		baseSizeStringValue,
		order.baseAssetAmount.precision
	);

	const existingOrderIsFullPosition = isEntirePositionOrder(
		order.baseAssetAmount
	);

	const isSizeEdited = !baseSizeBigNum.eq(order.baseAssetAmount);
	const isPrimaryPriceEdited =
		+priceBoxStringValue !== (+getInitialPrimaryPrice() as number);
	const isSecondaryPriceEdited =
		+secondaryPriceBoxStringValue !== +getInitialSecondaryPrice();

	const existingOrderDirection = (
		isSpot ? existingOrderDirectionSpot : existingOrderDirectionPerp
	) as 'long' | 'short' | 'buy' | 'sell';

	const quotePriceToUse = getQuotePriceToUse(
		existingOrderType.value,
		existingOrderDirection,
		markPriceToUse,
		oraclePrice as BigNum,
		priceBoxValueBigNum,
		markPriceToUse,
		secondaryPriceBoxValueBigNum
	);

	const [quoteSizeStringValue, setQuoteSizeStringValue] = useState(() =>
		quotePriceToUse.mul(baseSizeBigNum).shiftTo(QUOTE_PRECISION_EXP).toFixed(2)
	);

	const baseSizeForQuoteCalc =
		order.reduceOnly && existingOrderIsFullPosition
			? BigNum.from(
					openPositionInfo?.baseSize.abs() ?? ZERO,
					BASE_PRECISION_EXP
				)
			: BigNum.fromPrint(existingOrderSizeStringValue, BASE_PRECISION_EXP);

	const existingOrderQuoteValue = quotePriceToUse
		.mul(baseSizeForQuoteCalc)
		.shiftTo(QUOTE_PRECISION_EXP)
		.toNotional();

	const marginInfo = useCurrentAccountMarginInfo();
	const freeCollateral = marginInfo?.freeCollateral;

	// Determine max base size according to market type.
	// First perps...
	const perpTradeData: UseSyncPerpMaxLeverageAndFundsBreakdownTradeData = {
		side,
		orderType: existingOrderType.value,
		isFlippingPosition:
			(currentPositionSide === 'short' && side === 'buy') ||
			(currentPositionSide === 'long' && side === 'sell'),
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
	};
	const { maxBaseAvailable: maxBaseAvailablePerp } =
		useSyncPerpMaxLeverageAndFundsBreakdown({
			tradeData: perpTradeData,
			selectedMarket: uiMarket,
			perpMarketAccount: driftClient?.getPerpMarketAccount(
				marketId.marketIndex
			),
			currentUserClient,
			currentPosition: openPositionInfo,
			currentPositionDirection: currentPositionSide as 'long' | 'short',
			currentAccountFreeCollateral: freeCollateral,
		});

	// Then spot...
	const maxBaseAvailableSpot = (() => {
		if (!isSpot) {
			return 0;
		}

		const maxQuoteAvailableSpot =
			accountData?.client?.getMaxTradeSizeUSDCForSpot(
				marketIndex ?? 0,
				existingOrderDirection === 'buy'
					? PositionDirection.LONG
					: PositionDirection.SHORT
			);

		return BigNum.from(maxQuoteAvailableSpot ?? 0, QUOTE_PRECISION_EXP)
			.scale(100_000, (oraclePrice?.toNum() ?? 0) * 100_000)
			.toNum();
	})();

	const maxBaseAfterCancel =
		// Determine which to use...
		(isSpot ? maxBaseAvailableSpot : maxBaseAvailablePerp) +
		Number(existingOrderSizeStringValue);

	const maxBaseBigNum = order.reduceOnly
		? getReduceOnlyMax()
		: BigNum.fromPrint(
				`${maxBaseAfterCancel === Infinity ? 0 : maxBaseAfterCancel}`,
				BASE_PRECISION_EXP
			).abs();

	const maxBaseSizeString = roundToStepSizeIfLargeEnough(
		maxBaseBigNum.toNum().toString(),
		stepSize
	);

	const maxBaseRoundedToStepSizeBigNum = BigNum.fromPrint(
		maxBaseSizeString,
		BASE_PRECISION_EXP
	);

	const getSliderPercentage = (size: string) => {
		if (maxBaseBigNum.eqZero()) {
			return 0;
		}

		return +BigNum.fromPrint(size, BASE_PRECISION_EXP).toPercentage(
			maxBaseBigNum,
			BASE_PRECISION_EXP.toNumber()
		);
	};

	const [sliderPercentage, setSliderPercentage] = useState(() =>
		getSliderPercentage(existingOrderSizeStringValue)
	);

	// Get position entry price - fallback to mark price if no position exists
	const positionEntryPrice = openPositionInfo?.entryPrice
		? BigNum.from(openPositionInfo.entryPrice, PRICE_PRECISION_EXP)
		: markPriceToUse;

	// Check if stop loss is beyond liquidation price
	const shouldShowLiquidationWarning = openPositionInfo?.liqPrice
		? isStopLossBeyondLiquidationPrice(
				priceBoxValueBigNum,
				existingOrderType.value,
				BigNum.from(openPositionInfo.liqPrice, PRICE_PRECISION_EXP),
				currentPositionSide as 'long' | 'short'
			)
		: false;

	const errorMessageInfo = getMessageInfoForOrderViolation(
		existingOrderType.value,
		+priceBoxStringValue,
		markPriceToUse.toNum(),
		false,
		+secondaryPriceBoxStringValue,
		orderIsLong ? 'long' : 'short',
		oraclePrice?.toNum() as number,
		baseAssetSymbol,
		baseSizeBigNum.toNum(),
		stepSize,
		'editOrder'
	);

	const canConfirmEditOrder =
		!errorMessageInfo?.disableTradeButton &&
		(isSizeEdited ||
			isPrimaryPriceEdited ||
			isSecondaryPriceEdited ||
			newPrice) &&
		baseSizeBigNum.gtZero() &&
		!priceBoxValueBigNum.eqZero() &&
		(!showSecondaryPriceBox || !secondaryPriceBoxValueBigNum.eqZero()) &&
		!submitting;

	const handleSizeBoxUpdate = (
		value: string,
		inputSource: 'base' | 'quote'
	) => {
		const baseSizeBigNum = (() => {
			if (inputSource === 'base') {
				return BigNum.fromPrint(value, BASE_PRECISION_EXP);
			}

			return BigNum.fromPrint(value, QUOTE_PRECISION_EXP)
				.shift(BASE_PRECISION_EXP)
				.div(quotePriceToUse);
		})();

		const sizeToUse = baseSizeBigNum.gt(maxBaseBigNum)
			? maxBaseBigNum
			: baseSizeBigNum;

		const fitToStepSize = roundToStepSizeIfLargeEnough(
			sizeToUse.toNum().toString(),
			stepSize
		);

		setBaseSizeStringValue(fitToStepSize);

		setQuoteSizeStringValue(
			quotePriceToUse
				.mul(BigNum.fromPrint(fitToStepSize, BASE_PRECISION_EXP))
				.shiftTo(PRICE_PRECISION_EXP)
				.toFixed(2)
		);

		// Update slider value.
		const updatedSliderPercentage = getSliderPercentage(fitToStepSize);

		if (updatedSliderPercentage < 0) {
			setSliderPercentage(0);
			return;
		}

		if (updatedSliderPercentage > 99.99) {
			setSliderPercentage(100);
			return;
		}

		setSliderPercentage(updatedSliderPercentage);

		sizeSourceRef.current = 'input';
	};

	const handlePriceBoxUpdate = (
		value: string,
		updateQuote: boolean,
		variant: 'primary' | 'secondary'
	) => {
		const updateFn =
			variant === 'primary'
				? setPriceBoxStringValue
				: setSecondaryPriceBoxStringValue;
		updateFn(value);

		if (!updateQuote) {
			return;
		}

		const primaryValueToUse =
			variant === 'primary' ? value : priceBoxStringValue;
		const secondaryValueToUse =
			variant === 'secondary' ? value : secondaryPriceBoxStringValue;

		const newQuotePriceToUse = getQuotePriceToUse(
			existingOrderType.value,
			existingOrderDirection,
			markPriceToUse,
			oraclePrice as BigNum,
			BigNum.fromPrint(primaryValueToUse, PRICE_PRECISION_EXP),
			markPriceToUse,
			BigNum.fromPrint(secondaryValueToUse, PRICE_PRECISION_EXP)
		);

		setQuoteSizeStringValue(
			newQuotePriceToUse
				.mul(BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP))
				.shiftTo(PRICE_PRECISION_EXP)
				.toFixed(2)
		);

		sizeSourceRef.current = 'input';
	};

	const handleSliderValueUpdate = useCallback(
		(value: number) => {
			setSliderPercentage(value);

			// Update inputs.
			const scaledBaseSize = maxBaseRoundedToStepSizeBigNum.scale(value, 100);

			const fitToStepSize = roundToStepSizeIfLargeEnough(
				scaledBaseSize.abs().toNum().toString(),
				stepSize
			);

			setBaseSizeStringValue(fitToStepSize);

			setQuoteSizeStringValue(
				quotePriceToUse
					.mul(BigNum.fromPrint(fitToStepSize, BASE_PRECISION_EXP))
					.shiftTo(PRICE_PRECISION_EXP)
					.toFixed(2)
			);

			sizeSourceRef.current = 'slider';
		},
		[maxBaseRoundedToStepSizeBigNum.toNum(), quotePriceToUse.toNum(), stepSize]
	);

	// Auto-update size values for oracle offset orders if last set with the slider.
	// This will happen based off `handleSliderValueUpdate`'s deps.
	const sliderPercentageRef = useSyncedRef(sliderPercentage);
	useEffect(() => {
		if (sizeSourceRef.current === 'input' || !isOracleOffsetOrder) {
			return;
		}

		handleSliderValueUpdate(sliderPercentageRef.current);
	}, [isOracleOffsetOrder, handleSliderValueUpdate, sliderPercentageRef]);

	useEffect(() => {
		if (+maxBaseSizeString < +baseSizeStringValue && order.reduceOnly) {
			handleSizeBoxUpdate(maxBaseSizeString, 'base');
		}
	}, [maxBaseSizeString]);

	const handleConfirmEditOrder = async () => {
		if (
			ENUM_UTILS.match(order.orderType, OrderType.MARKET) ||
			ENUM_UTILS.match(order.orderType, OrderType.ORACLE)
		) {
			// don't allow editing market or oracle orders
			return;
		}

		setSubmitting(true);

		const newBaseAmount = existingOrderIsFullPosition
			? getMaxLeverageOrderSize(order.baseAssetAmount)
			: baseSizeBigNum;

		const toastId = `edit-order-${
			existingOrderType.value
		}-${existingOrderDirection}-${Date.now()}`;

		const success = await editNonMarketOrder(
			toastId,
			order,
			previewOrderParams.slippage,
			{
				baseAmount: newBaseAmount,
				limitPrice: isLimitOnlyOrder
					? priceBoxValueBigNum
					: isTriggerLimitOrder
						? secondaryPriceBoxValueBigNum
						: BigNum.zero(),
				triggerPrice: isTriggerOrder ? priceBoxValueBigNum : BigNum.zero(),
				oraclePriceOffset: isOracleOffsetOrder
					? Number(priceBoxStringValue)
					: undefined,
			}
		);

		if (success) {
			onClose();
		} else {
			// If it fails, the user will get a toast notification from the editOpenOrder action
			setSubmitting(false);
		}
	};

	function getReduceOnlyMax(): BigNum {
		if (isSpot) {
			const currentPositionDeposit = currentUserSpotBalances.find(
				(bal) =>
					bal.asset.marketIndex === order.marketIndex &&
					matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
			)?.balance;

			const currentPositionBorrow = currentUserSpotBalances.find(
				(bal) =>
					bal.asset.marketIndex === order.marketIndex &&
					matchEnum(bal.balanceType, SpotBalanceType.BORROW)
			)?.balance;

			if (
				(!currentPositionBorrow && !currentPositionDeposit) ||
				(currentPositionBorrow?.eqZero() && currentPositionDeposit?.eqZero())
			) {
				return BigNum.zero(order.baseAssetAmount.precision);
			} else if (existingOrderDirectionSpot === 'sell') {
				return currentPositionDeposit?.abs() as BigNum;
			} else {
				return (
					currentPositionBorrow?.abs() ??
					BigNum.zero(order.baseAssetAmount.precision)
				);
			}
		} else {
			const currentPerpPositionSize: BN = currentUserPerpPositions?.find(
				(pos) => pos.marketIndex === order.marketIndex
			)?.baseSize as BN;

			if (!currentPerpPositionSize) return BigNum.zero(BASE_PRECISION_EXP);

			if (existingOrderDirectionPerp === 'short') {
				return currentPerpPositionSize.isNeg()
					? BigNum.zero(BASE_PRECISION_EXP)
					: BigNum.from(currentPerpPositionSize, BASE_PRECISION_EXP)?.abs();
			} else {
				return currentPerpPositionSize.isNeg()
					? BigNum.from(currentPerpPositionSize, BASE_PRECISION_EXP)?.abs()
					: BigNum.zero(BASE_PRECISION_EXP);
			}
		}
	}

	return (
		<>
			<ResponsiveModal.Body className="gap-y-lg">
				{/* Order Details Section */}
				<div className={TRADE_CONFIRMATION_MODAL_DETAILS_CONTAINER_CLASSNAME}>
					<ValueDisplay.Default
						label="Action"
						value={`${
							existingOrderDirection === 'sell' ||
							existingOrderDirection === 'short'
								? 'Sell/Short'
								: 'Buy/Long'
						} ${baseAssetSymbol}-${isSpot ? 'SPOT' : 'PERP'}`}
						valueClassName={
							existingOrderDirection === 'sell' ||
							existingOrderDirection === 'short'
								? 'text-text-negative-red-button'
								: 'text-text-positive-green-button'
						}
					/>
					<div
						onClick={() => {
							const newValue = inputSource === 'base' ? 'quote' : 'base';
							setInputSource(newValue);
						}}
						className="hover:bg-container-bg-secondary flex cursor-pointer items-center justify-between transition-colors"
					>
						<div className="flex-1">
							<ValueDisplay.Default
								label={inputSource === 'quote' ? 'Value' : 'Size'}
								value={
									inputSource === 'quote'
										? `${existingOrderQuoteValue}`
										: `${
												order.reduceOnly && existingOrderIsFullPosition
													? BigNum.from(
															openPositionInfo?.baseSize.abs() ?? ZERO,
															BASE_PRECISION_EXP
														).prettyPrint()
													: existingOrderSizeStringValue
											} ${baseAssetSymbol}`
								}
							/>
						</div>
						<Swap size={16} className="ml-1 text-text-tertiary" />
					</div>
					<ValueDisplay.Default
						label="Order Type"
						value={existingOrderType.label}
					/>
					<ValueDisplay.Default
						label="Reference Price"
						value={markPriceToUse.toNotional(
							false,
							undefined,
							marketPriceDecimals
						)}
					/>
					{openPositionInfo?.liqPrice.gt(ZERO) && (
						<ValueDisplay.Default
							label="Liquidation Price"
							value={BigNum.from(
								openPositionInfo.liqPrice,
								PRICE_PRECISION_EXP
							).toNotional(false, undefined, marketPriceDecimals)}
						/>
					)}
				</div>

				<div className="flex flex-col gap-y-md">
					{/* Order Size Display */}
					{(existingOrderType.value === 'limit' ||
						existingOrderType.value === 'oracleLimit') &&
						!isTriggerOrder && (
							<MaxSizeLabel
								currentPositionBaseSizeString={
									order.reduceOnly &&
									existingOrderIsFullPosition &&
									openPositionInfo
										? BigNum.from(
												openPositionInfo.baseSize.abs(),
												BASE_PRECISION_EXP
											).prettyPrint()
										: existingOrderSizeStringValue
								}
								freeCollateral={
									freeCollateral
										? BigNum.from(freeCollateral, QUOTE_PRECISION_EXP).toNum()
										: 0
								}
								formattedMarketSymbol={baseAssetSymbol}
								reduceOnly={order.reduceOnly}
								stepSize={stepSize}
								marginMode={marginMode}
							/>
						)}

					{/* Input Fields */}
					<div className="flex flex-col gap-y-sm">
						{existingOrderType.value !== 'market' && (
							<PriceInput
								label={priceInputLabel}
								value={priceBoxStringValue}
								handleChange={(value) =>
									handlePriceBoxUpdate(value, !showSecondaryPriceBox, 'primary')
								}
								marketIndex={marketIndex}
								marketType={marketType}
								allowNegative={existingOrderType.value === 'oracleLimit'}
								baseAssetSymbol={baseAssetSymbol}
								showMidButton={isLimitOnlyOrder}
							/>
						)}

						{showSecondaryPriceBox && (
							<PriceInput
								label="Limit Price"
								value={secondaryPriceBoxStringValue}
								handleChange={(value) =>
									handlePriceBoxUpdate(value, true, 'secondary')
								}
								marketIndex={marketIndex}
								marketType={marketType}
								baseAssetSymbol={baseAssetSymbol}
								showMidButton={isTriggerLimitOrder}
							/>
						)}

						<SizeInput
							baseSizeStringValue={baseSizeStringValue}
							quoteSizeStringValue={quoteSizeStringValue}
							onBaseSizeChange={(value) => handleSizeBoxUpdate(value, 'base')}
							onQuoteSizeChange={(value) => handleSizeBoxUpdate(value, 'quote')}
							disabled={existingOrderIsFullPosition}
							formattedMarketSymbol={baseAssetSymbol}
							stepSize={stepSize}
						/>

						{!existingOrderIsFullPosition && (
							<div className="flex items-center">
								<Slider
									value={sliderPercentage}
									onValueChange={handleSliderValueUpdate}
									onValueCommit={handleSliderValueUpdate}
								/>
							</div>
						)}
					</div>

					{/* Estimated P&L */}
					{openPositionInfo &&
						!priceBoxValueBigNum.eqZero() &&
						!baseSizeBigNum.eqZero() &&
						!(existingOrderType.value === 'limit' && !isTriggerOrder) && (
							<div className="flex items-center justify-between gap-x-1">
								<span className="typo-body-xs text-text-secondary">
									Est. P&L:
								</span>
								<TpSlOrderFormEstimatedPnL
									className="typo-body-xs"
									currentPositionSize={BigNum.from(
										openPositionInfo.baseSize.abs(),
										BASE_PRECISION_EXP
									)}
									currentPositionEntryPrice={positionEntryPrice}
									currentPositionDirection={
										currentPositionSide as 'long' | 'short'
									}
									side={side}
									limitExitSize={baseSizeBigNum}
									limitExitPrice={
										existingOrderType.value === 'market'
											? markPriceToUse
											: showSecondaryPriceBox &&
												  !secondaryPriceBoxValueBigNum.eqZero()
												? secondaryPriceBoxValueBigNum
												: priceBoxValueBigNum
									}
									isMarketOrder={existingOrderType.value === 'market'}
								/>
							</div>
						)}
				</div>

				{(errorMessageInfo?.shouldShowMessage ||
					shouldShowLiquidationWarning) && (
					<div className="flex flex-col gap-y-2">
						{errorMessageInfo?.shouldShowMessage && (
							<InlineAlert
								variant={errorMessageInfo.messageType}
								title={errorMessageInfo.messageTitle}
							/>
						)}

						{/* Show liquidation warning even when other errors exist */}
						{shouldShowLiquidationWarning && (
							<InlineAlert
								variant="warning"
								title={
									currentPositionSide === 'long'
										? 'Stop loss is recommended to be above the estimated liquidation price'
										: 'Stop loss is recommended to be below the estimated liquidation price'
								}
							/>
						)}
					</div>
				)}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					className="w-full"
					onClick={handleConfirmEditOrder}
					disabled={!canConfirmEditOrder}
					isLoading={submitting}
					dataTestId="edit-order-modal-submit"
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</>
	);
};
